/**
 * Generic Russian City Landing Page
 * Handles all /ru/:citySlug routes
 */
import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { RussianHero } from "@/components/RussianHero";
import { RussianWhyUs } from "@/components/RussianWhyUs";
import { RussianTestimonials } from "@/components/RussianTestimonials";
import { RussianFAQ } from "@/components/RussianFAQ";
import { RussianFinalCTA } from "@/components/RussianFinalCTA";
import { LocationContext, REGIONS } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { RussianServices } from "@/components/RussianServices";
import { RussianReferFriend } from "@/components/RussianReferFriend";

const CITY_DATA: Record<string, { name: string; ruName: string; regionSlug: keyof typeof REGIONS; testimonialRegion: "sacramento" | "la" | "san-francisco" | "san-jose" | "san-diego" | "bay-area" }> = {
  // Region hubs
  "contra-costa":      { name: "Contra Costa County",  ruName: "Округ Контра Коста",    regionSlug: "contra-costa",    testimonialRegion: "bay-area" },
  "solano":            { name: "Solano County",         ruName: "Округ Солано",          regionSlug: "solano",          testimonialRegion: "bay-area" },
  "alameda":           { name: "Alameda County",        ruName: "Округ Аламеда",         regionSlug: "alameda",         testimonialRegion: "bay-area" },
  "san-francisco":     { name: "San Francisco",         ruName: "Сан-Франциско",         regionSlug: "san-francisco",   testimonialRegion: "san-francisco" },
  "santa-clara":       { name: "Santa Clara County",    ruName: "Округ Санта-Клара",     regionSlug: "santa-clara",     testimonialRegion: "san-jose" },
  "marin":             { name: "Marin County",          ruName: "Округ Марин",           regionSlug: "marin",           testimonialRegion: "san-francisco" },
  "orange-county":     { name: "Orange County",         ruName: "Округ Ориндж",          regionSlug: "orange-county",   testimonialRegion: "san-diego" },
  "los-angeles":       { name: "Los Angeles County",    ruName: "Округ Лос-Анджелес",    regionSlug: "los-angeles",     testimonialRegion: "la" },
  "san-diego":         { name: "San Diego",             ruName: "Сан-Диего",             regionSlug: "san-diego",       testimonialRegion: "san-diego" },
  // Sacramento Area
  "sacramento":        { name: "Sacramento",        ruName: "Сакраменто",        regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "roseville":         { name: "Roseville",         ruName: "Розвилл",           regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "folsom":            { name: "Folsom",            ruName: "Фолсом",            regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "elk-grove":         { name: "Elk Grove",         ruName: "Элк Гров",          regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "davis":             { name: "Davis",             ruName: "Дэвис",             regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "rocklin":           { name: "Rocklin",           ruName: "Рослин",            regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "granite-bay":       { name: "Granite Bay",       ruName: "Гранит Бэй",        regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "el-dorado-hills":   { name: "El Dorado Hills",   ruName: "Эль Дорадо Хиллс",  regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "west-sacramento":   { name: "West Sacramento",   ruName: "Западный Сакраменто", regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "rancho-cordova":    { name: "Rancho Cordova",    ruName: "Ранчо Кордова",      regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "carmichael":        { name: "Carmichael",        ruName: "Кармайкл",          regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "citrus-heights":    { name: "Citrus Heights",    ruName: "Ситрус Хайтс",      regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  // LA Cities
  "santa-monica":      { name: "Santa Monica",      ruName: "Санта-Моника",      regionSlug: "los-angeles", testimonialRegion: "la" },
  "pasadena":          { name: "Pasadena",          ruName: "Пасадена",          regionSlug: "los-angeles", testimonialRegion: "la" },
  "manhattan-beach":   { name: "Manhattan Beach",   ruName: "Манхэттен-Бич",     regionSlug: "los-angeles", testimonialRegion: "la" },
  "long-beach":        { name: "Long Beach",        ruName: "Лонг-Бич",          regionSlug: "los-angeles", testimonialRegion: "la" },
  "encino":            { name: "Encino",            ruName: "Энсино",            regionSlug: "los-angeles", testimonialRegion: "la" },
  "studio-city":       { name: "Studio City",       ruName: "Студио Сити",       regionSlug: "los-angeles", testimonialRegion: "la" },
  "woodland-hills":    { name: "Woodland Hills",    ruName: "Вудленд-Хиллс",     regionSlug: "los-angeles", testimonialRegion: "la" },
  // Other Cities
  "oakland":           { name: "Oakland",           ruName: "Окленд",            regionSlug: "alameda", testimonialRegion: "bay-area" },
  "berkeley":          { name: "Berkeley",          ruName: "Беркли",            regionSlug: "alameda", testimonialRegion: "bay-area" },
  "fremont":           { name: "Fremont",           ruName: "Фримонт",           regionSlug: "alameda", testimonialRegion: "bay-area" },
  "san-jose":          { name: "San Jose",          ruName: "Сан-Хосе",          regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "palo-alto":         { name: "Palo Alto",         ruName: "Пало-Альто",        regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "sunnyvale":         { name: "Sunnyvale",         ruName: "Саннивейл",         regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "mountain-view":     { name: "Mountain View",     ruName: "Маунтин-Вью",       regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "cupertino":         { name: "Cupertino",         ruName: "Купертино",         regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "walnut-creek":      { name: "Walnut Creek",      ruName: "Уолнат-Крик",       regionSlug: "contra-costa", testimonialRegion: "bay-area" },
  "concord":           { name: "Concord",           ruName: "Конкорд",           regionSlug: "contra-costa", testimonialRegion: "bay-area" },
  "pleasanton":        { name: "Pleasanton",        ruName: "Плезантон",         regionSlug: "alameda", testimonialRegion: "bay-area" },
  "dublin":            { name: "Dublin",            ruName: "Дублин",            regionSlug: "alameda", testimonialRegion: "bay-area" },
  "livermore":         { name: "Livermore",         ruName: "Ливермор",          regionSlug: "alameda", testimonialRegion: "bay-area" },
  "irvine":            { name: "Irvine",            ruName: "Ирвайн",            regionSlug: "orange-county", testimonialRegion: "san-diego" },
  "anaheim":           { name: "Anaheim",           ruName: "Анахайм",           regionSlug: "orange-county", testimonialRegion: "san-diego" },
  "huntington-beach":  { name: "Huntington Beach",  ruName: "Хантингтон-Бич",    regionSlug: "orange-county", testimonialRegion: "san-diego" },
};

const RussianCityPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const cityData = citySlug ? CITY_DATA[citySlug] : null;
  if (!cityData) return <Navigate to="/ru/sacramento" replace />;

  const { ruName, name, regionSlug, testimonialRegion } = cityData;

  return (
    <LocationContext.Provider value={{ slug: citySlug!, name, type: "city", regionSlug }}>
      <SEO
        title={`Уборка дома в ${ruName} — Uplevel Cleaning`}
        description={`Профессиональный сервис уборки дома в ${ruName}. Фиксированные цены, бронирование за 60 секунд, гарантия удовлетворённости 200%.`}
        keywords={`уборка дома ${ruName}, клининг ${ruName}, генеральная уборка ${ruName}, уборка квартиры ${ruName}, uplevel cleaning`}
        url={`https://uplevelcleaning.com/ru/${citySlug}`}
        alternateUrl={`https://uplevelcleaning.com/locations/${citySlug}`}
        alternateLang="en-US"
        locationName={name}
        regionSlug={regionSlug}
        lang="ru"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Главная", url: "/" },
          { name: "На русском", url: "/ru/sacramento" },
          { name: ruName, url: `/ru/${citySlug}` }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="ru">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <RussianHero cityName={ruName} regionSlug={regionSlug} />
          <RussianWhyUs cityName={ruName} />
          <RussianServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <RussianTestimonials region={testimonialRegion} />
          <Guarantee />
          <RussianReferFriend />
          <RussianFAQ cityName={ruName} />
          <RussianFinalCTA cityName={ruName} />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default RussianCityPage;
